import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isSameDay } from 'date-fns';
import { toDatestampString } from '~/models/time';
import { AD_HOC_PAYMENT_DEFAULTS, netTermsToOption } from '../AdHocBillModal.types';
import { useAdHocScheduledData } from './useAdHocScheduledData';
import { useInvoiceDraftDates } from './useInvoiceDraftDates';
const isOccupiedDate = (date, invoiceDraftDates)=>{
    var _ref;
    return (_ref = invoiceDraftDates === null || invoiceDraftDates === void 0 ? void 0 : invoiceDraftDates.dates().some((d)=>isSameDay(date, d))) !== null && _ref !== void 0 ? _ref : false;
};
const scheduledDateParam = (issueDate, invoiceDraftDates)=>{
    if (!isOccupiedDate(issueDate, invoiceDraftDates)) return null;
    return toDatestampString(issueDate);
};
export const useAdHocDateSync = (param)=>{
    let { relationshipId, form, defaultEmailSubject, defaultEmailContent } = param;
    const { invoiceDraftDates } = useInvoiceDraftDates(relationshipId, {
        refreshOnHookMount: true
    });
    const issueDate = form.watch('issueDate');
    const setValue = form.setValue;
    const dateParam = useMemo(()=>scheduledDateParam(issueDate, invoiceDraftDates), [
        issueDate,
        invoiceDraftDates
    ]);
    const isDateOccupied = dateParam != null;
    const { scheduledData, isLoading: isScheduledDataLoading } = useAdHocScheduledData(relationshipId, dateParam);
    const appliedForDateRef = useRef(null);
    useEffect(()=>{
        appliedForDateRef.current = null;
    }, [
        dateParam
    ]);
    useEffect(()=>{
        if (dateParam != null && scheduledData != null && appliedForDateRef.current !== dateParam) {
            appliedForDateRef.current = dateParam;
            setValue('coverCreditCardFees', scheduledData.coverCreditCardFees);
            setValue('netTerms', netTermsToOption(scheduledData.netTerms));
            if (scheduledData.legalTermsDocument != null) {
                setValue('includeTermsOfService', true);
                setValue('termsOfServiceId', scheduledData.legalTermsDocument.legalTermsId);
            } else {
                setValue('includeTermsOfService', false);
                setValue('termsOfServiceId', '');
            }
            if (scheduledData.issuedEmail != null) {
                setValue('emailSubject', scheduledData.issuedEmail.subject);
                setValue('emailContent', scheduledData.issuedEmail.content);
            } else {
                setValue('emailSubject', defaultEmailSubject);
                setValue('emailContent', defaultEmailContent);
            }
        } else if (dateParam == null && appliedForDateRef.current != null) {
            appliedForDateRef.current = null;
            setValue('coverCreditCardFees', AD_HOC_PAYMENT_DEFAULTS.coverCreditCardFees);
            setValue('netTerms', AD_HOC_PAYMENT_DEFAULTS.netTerms);
            setValue('includeTermsOfService', AD_HOC_PAYMENT_DEFAULTS.includeTermsOfService);
            setValue('termsOfServiceId', AD_HOC_PAYMENT_DEFAULTS.termsOfServiceId);
            setValue('emailSubject', defaultEmailSubject);
            setValue('emailContent', defaultEmailContent);
        }
    }, [
        dateParam,
        scheduledData,
        setValue,
        defaultEmailSubject,
        defaultEmailContent
    ]);
    const highlightDay = useCallback((day)=>isOccupiedDate(day, invoiceDraftDates), [
        invoiceDraftDates
    ]);
    return {
        highlightDay,
        isDateOccupied,
        isScheduledDataLoading
    };
};
